import React, { useState, useEffect } from 'react';
import api from '@/config/axios';
import { DollarSign, Users, Building2, TrendingUp, Filter, Calendar, Download, Trash2 } from 'lucide-react';
import { useToastContext } from '../ui/feedback/ToastProvider';
import { useConfirmContext } from '../ui/feedback/ConfirmProvider';
import { KPICard, Card, Spinner, Badge, DataTable, Button, FormDatePicker, Divider } from '../ui';
import DirhamSymbol from '../components/DirhamSymbol';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ProfitAllocation = () => {
  const { error: showError, success: showSuccess } = useToastContext();
  const { confirm } = useConfirmContext();
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    allocationType: 'all',
    recipientId: '',
    status: 'all',
  });
  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState({
    total_allocated_usdt: 0,
    total_allocated_aed: 0,
    total_paid_out: 0,
    total_pending: 0,
    by_type: {},
  });

  useEffect(() => {
    fetchAllocations();
  }, []);

  useEffect(() => {
    generateChartData();
    calculateSummary();
  }, [allocations]);

  const fetchAllocations = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.allocationType !== 'all') params.allocation_type = filters.allocationType;
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.recipientId) params.recipient_id = filters.recipientId;

      const response = await api.get('/api/profit-allocations', { params });
      setAllocations(response.data.allocations || []);
    } catch (error) {
      console.error('Error fetching profit allocations:', error);
      showError('Failed to fetch profit allocations');
      setAllocations([]);
    } finally {
      setLoading(false);
    }
  };

  const generateChartData = () => {
    // Group by date and allocation type
    const grouped = {};
    
    allocations.forEach(allocation => {
      const date = new Date(allocation.date).toISOString().split('T')[0];
      if (!grouped[date]) {
        grouped[date] = {
          date,
          investor_profit: 0,
          broker_commission: 0,
          admin_profit: 0,
          extra_profit: 0,
        };
      }
      
      const type = allocation.allocation_type;
      if (grouped[date][type] !== undefined) {
        grouped[date][type] += allocation.amount_aed || 0;
      }
    });

    const data = Object.values(grouped).sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );

    setChartData(data);
  };

  const calculateSummary = () => {
    const sum = {
      total_allocated_usdt: 0,
      total_allocated_aed: 0,
      total_paid_out: 0,
      total_pending: 0,
      by_type: {
        investor_profit: 0,
        broker_commission: 0,
        admin_profit: 0,
        extra_profit: 0,
      },
    };

    allocations.forEach(allocation => {
      sum.total_allocated_usdt += allocation.amount_usdt || 0;
      sum.total_allocated_aed += allocation.amount_aed || 0;
      sum.total_paid_out += allocation.total_paid_out || 0;
      
      if (allocation.payout_status === 'pending') {
        sum.total_pending += allocation.amount_usdt || 0;
      }

      const type = allocation.allocation_type;
      if (sum.by_type[type] !== undefined) {
        sum.by_type[type] += allocation.amount_aed || 0;
      }
    });

    setSummary(sum);
  };

  const handleApplyFilters = () => {
    fetchAllocations();
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: null,
      endDate: null,
      allocationType: 'all',
      recipientId: '',
      status: 'all',
    });
    fetchAllocations();
  };

  const handleDelete = async (allocation) => {
    const confirmed = await confirm({
      title: 'Delete Profit Allocation',
      message: `Are you sure you want to delete this allocation for ${allocation.recipient_name}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });

    if (!confirmed) return;

    try {
      await api.delete(`/api/profit-allocations/${allocation._id}`);
      showSuccess('Profit allocation deleted successfully');
      fetchAllocations();
    } catch (error) {
      console.error('Error deleting profit allocation:', error);
      showError(error.response?.data?.message || 'Failed to delete profit allocation');
    }
  };

  const getAllocationTypeColor = (type) => {
    switch (type) {
      case 'investor_profit':
        return 'primary';
      case 'broker_commission':
        return 'warning';
      case 'admin_profit':
        return 'success';
      case 'extra_profit':
        return 'info';
      default:
        return 'default';
    }
  };

  const getPayoutStatusColor = (status) => {
    switch (status) {
      case 'fully_paid':
        return 'success';
      case 'partially_paid':
        return 'warning';
      case 'pending':
        return 'error';
      default:
        return 'default';
    }
  };

  const tableColumns = [
    {
      key: 'date',
      label: 'Date',
      sortKey: 'date',
      render: (allocation) => new Date(allocation.date).toLocaleDateString(),
    },
    {
      key: 'allocation_type',
      label: 'Type',
      render: (allocation) => (
        <Badge variant={getAllocationTypeColor(allocation.allocation_type)} size="sm">
          {allocation.allocation_type.replace('_', ' ').toUpperCase()}
        </Badge>
      ),
    },
    {
      key: 'recipient',
      label: 'Recipient',
      render: (allocation) => (
        <div>
          <div className="font-medium">{allocation.recipient_name}</div>
          <div className="text-xs text-gray-500">{allocation.recipient_email}</div>
        </div>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      sortKey: 'amount_aed',
      render: (allocation) => (
        <div className="flex flex-col gap-0.5">
          <span className="flex items-center gap-1 font-semibold">
            <DirhamSymbol size={14} inline />
            {allocation.amount_aed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
          <span className="text-xs text-gray-500">
            ${allocation.amount_usdt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
          </span>
        </div>
      ),
    },
    {
      key: 'payout_status',
      label: 'Payout Status',
      render: (allocation) => (
        <Badge variant={getPayoutStatusColor(allocation.payout_status)} size="sm">
          {allocation.payout_status.replace('_', ' ').toUpperCase()}
        </Badge>
      ),
    },
    {
      key: 'balance',
      label: 'Balance Remaining',
      sortKey: 'balance_remaining',
      render: (allocation) => (
        <span className="flex items-center gap-1">
          ${allocation.balance_remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (allocation) => (
        <button
          onClick={() => handleDelete(allocation)}
          className="p-2 text-error-600 dark:text-error-400 hover:text-error-800 dark:hover:text-error-200 hover:bg-error-100 dark:hover:bg-error-900/20 rounded-lg transition-colors"
          title="Delete"
        >
          <Trash2 size={18} />
        </button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <DollarSign className="text-primary-600 dark:text-primary-400" size={28} />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-primary-900 dark:text-primary-50">
              Profit Allocation
            </h1>
            <p className="text-primary-600 dark:text-primary-400 mt-1.5 text-sm sm:text-base">
              Track and manage profit distributions
            </p>
          </div>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
        <KPICard
          title="Total Allocated"
          value={
            <span className="flex items-center gap-1">
              <DirhamSymbol size={20} inline />
              {summary.total_allocated_aed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          }
          icon={<DollarSign size={24} />}
          color="primary"
        />
        <KPICard
          title="Total Paid Out"
          value={`$${summary.total_paid_out.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT`}
          icon={<TrendingUp size={24} />}
          color="success"
        />
        <KPICard
          title="Pending Payouts"
          value={`$${summary.total_pending.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USDT`}
          icon={<Calendar size={24} />}
          color="warning"
        />
        <KPICard
          title="Total Records"
          value={allocations.length}
          icon={<Filter size={24} />}
          color="info"
        />
      </div>

      {/* Allocation by Type */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-primary-900 dark:text-primary-50 mb-4">
            Allocation by Type
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
              <div className="text-sm text-primary-600 dark:text-primary-400">Investor Profit</div>
              <div className="flex items-center gap-1 text-lg font-semibold mt-1">
                <DirhamSymbol size={16} inline />
                {summary.by_type.investor_profit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <div className="text-sm text-orange-600 dark:text-orange-400">Broker Commission</div>
              <div className="flex items-center gap-1 text-lg font-semibold mt-1">
                <DirhamSymbol size={16} inline />
                {summary.by_type.broker_commission.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="text-sm text-green-600 dark:text-green-400">Admin Profit</div>
              <div className="flex items-center gap-1 text-lg font-semibold mt-1">
                <DirhamSymbol size={16} inline />
                {summary.by_type.admin_profit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="text-sm text-blue-600 dark:text-blue-400">Extra Profit</div>
              <div className="flex items-center gap-1 text-lg font-semibold mt-1">
                <DirhamSymbol size={16} inline />
                {summary.by_type.extra_profit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Chart */}
      {chartData.length > 0 && (
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-primary-900 dark:text-primary-50 mb-4">
              Allocation Trends
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="investor_profit" stackId="1" stroke="#3b82f6" fill="#3b82f6" name="Investor Profit" />
                <Area type="monotone" dataKey="broker_commission" stackId="1" stroke="#f59e0b" fill="#f59e0b" name="Broker Commission" />
                <Area type="monotone" dataKey="admin_profit" stackId="1" stroke="#10b981" fill="#10b981" name="Admin Profit" />
                <Area type="monotone" dataKey="extra_profit" stackId="1" stroke="#6366f1" fill="#6366f1" name="Extra Profit" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-primary-900 dark:text-primary-50 mb-4">
            Filters
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
            <div>
              <FormDatePicker
                label="Start Date"
                value={filters.startDate}
                onChange={(value) => setFilters({ ...filters, startDate: value })}
              />
            </div>
            <div>
              <FormDatePicker
                label="End Date"
                value={filters.endDate}
                onChange={(value) => setFilters({ ...filters, endDate: value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Allocation Type
              </label>
              <select
                value={filters.allocationType}
                onChange={(e) => setFilters({ ...filters, allocationType: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="all">All Types</option>
                <option value="investor_profit">Investor Profit</option>
                <option value="broker_commission">Broker Commission</option>
                <option value="admin_profit">Admin Profit</option>
                <option value="extra_profit">Extra Profit</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="processed">Processed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <Button onClick={handleApplyFilters} leftIcon={<Filter size={16} />} className="w-full">
                Apply
              </Button>
            </div>
            <div>
              <Button variant="outline" onClick={handleResetFilters} className="w-full">
                Reset
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Allocations Table */}
      <Card>
        <DataTable
          data={allocations}
          columns={tableColumns}
          title="Profit Allocations"
          searchable
          exportable
          exportFilename="profit-allocations"
        />
      </Card>
    </div>
  );
};

export default ProfitAllocation;
